/* eslint-disable quotes */
import React from "react";
import Select from "react-select";
import { set, get, find } from "lodash";
import { useTranslation } from "react-i18next";
import {
  calculateTotValues,
  SelectBoxStyles,
  checkIsNumber,
  cloneData,
  checkCurrency,
  roundOff,
  shortenStringWithTooltip,
} from "../../../../../Components/helper";
import useShowHideSectionHooks from "../Hooks/useShowHideSectionHooks";
import useValidationHooks from "../Hooks/useValidationHooks";
import { testingFieldpath, errorpath } from "../../../ProductCosting/fieldPath";
import { NewCheckbox } from '../../../../../Components/PIM/common/NewCheckbox';

export function TestingTable({ productInfoObj }) {
  const { productCostingstate, setProductCostingState, globalError } = productInfoObj;
  const {
    colourwayOptionsArr,
    testing: { testingInfo, sumOfPrice },
    productInfo: { currency },
  } = productCostingstate;
  const { testing } = globalError;
  const { t } = useTranslation("common");

  const { onChangeCheckIfErrorExist } = useValidationHooks();

  const { statusState, controlAmberColour, isDisabled, markAsCompleteState } = useShowHideSectionHooks(productInfoObj);

  const calculateTotPrice = async (copyState, key) => {
    const testData = copyState.testing.testingInfo.filter((val) => !val.isExcluded);
    const data = await calculateTotValues(testData, [{ key: "totalPrice", updateKey: "sumOfPrice" }]);
    set(copyState, testingFieldpath().SUM_PRICE, data[key]);
    setProductCostingState((state) => ({ ...state, ...copyState }));
  };

  const calculatePricePerUnit = async (copyState, value, index, pricePerPath) => {
    const quantitiesArray = get(copyState, "quantities.quantitiesInfo");
    if (Array.isArray(quantitiesArray) && quantitiesArray.length > 0 && copyState.testing.testingInfo[index]?.colourway) {
      const findColourData = find(quantitiesArray, (o) => {
        return o.colourway._id === copyState.testing.testingInfo[index]?.colourway._id;
      });
      if (findColourData) {
        const pricePerColour = value / findColourData.sumOfQuantityPerSizCol;
        set(copyState, pricePerPath, pricePerColour);
      } else set(copyState, pricePerPath, 0);
      const data = cloneData(copyState.testing.testingInfo);
      set(copyState, testingFieldpath().TESTING_INFO_DATA, data);
      setProductCostingState((state) => ({ ...state, ...copyState }));
    }
  };

  const handleInputChange = async (value, path, supplierPath, index, pricePerPath) => {
    const copyState = productCostingstate;
    set(copyState, path, value);

    if (supplierPath) {
      set(copyState, supplierPath, false);
    }

    setProductCostingState((state) => ({ ...state, ...copyState }));
    await calculateTotPrice(copyState, "sumOfPrice");
    const values = copyState.testing.testingInfo[index]?.totalPrice;
    await calculatePricePerUnit(copyState, values, index, pricePerPath);
  };

  const updateError = (path) => onChangeCheckIfErrorExist(path, globalError);

  const renderComponent = Array.isArray(testingInfo) && testingInfo.length > 0;

  return (
    <div className="table-responsive average-colourway-table table-max-h-50">
      <table className="table common-custom-table layout-fixed">
        <thead>
          <tr>
            <th className="w-150px">{t("COSTING_TO_DO_LIST.COLORWAY")}</th>
            <th className="w-150px">{t("FILTER.TESTING_CODE")}</th>
            <th className="w-250px">{t("FILTER.TESTING_DESCRIPTION")}</th>
            <th className="w-150px">{t("TAKING_DATES.TESTING_TYPE")}</th>
            <th className="w-150px">{t("TAKING_DATES.BOM_COMPONENT")}</th>
            <th className="w-150px">{t("TAKING_DATES.COLOR_REF")}</th>
            <th className="w-150px">{t("FILTER.TEST_PRICE")}</th>
            <th className="w-150px">{t("FILTER.PRICE_PER_UNITS")}</th>
            <th className="w-100px">{t("TAKING_DATES.EXCLUDE")}</th>
          </tr>
        </thead>
        <tbody>
          {renderComponent &&
            testingInfo.map((data, index) => {
              return (
                <tr key={index}>
                  <td>
                    {isDisabled ? (
                      data?.colourway?.name
                    ) : (
                      <div className="mt-3 common-react-select">
                        <Select
                          options={colourwayOptionsArr}
                          onChange={(selected) => {
                            handleInputChange(selected, testingFieldpath(index).SELECTED_COLOURWAY, undefined, index, testingFieldpath(index).PRICE_PER_UNIT);
                            if (get(testing, `[${index}].colourway`, "")) {
                              updateError(errorpath(index).TESTING.ERROR_COLOURWAY);
                            }
                          }}
                          getOptionLabel={(option) => option.name}
                          getOptionValue={(option) => option.name}
                          value={data.colourway}
                          isDisabled={isDisabled}
                          styles={SelectBoxStyles}
                          menuPosition="fixed"
                          menuShouldBlockScroll
                          menuShouldScrollIntoView
                          className="common-select"
                          classNamePrefix="common-select"
                        />
                        <span className="error-message">{get(testing, `[${index}].colourway`)}</span>
                      </div>
                    )}
                  </td>
                  <td>{data?.testingCode?.code}</td>
                  <td>
                    <div className="text-truncate w-250px">
                      <span title={data?.testingDesc}>{shortenStringWithTooltip(data.testingDesc, 30)}</span>
                    </div>
                  </td>
                  <td>
                    <div className="text-truncate w-150px">
                      <span title={data?.testingType?.name}>{data?.testingType?.name}</span>
                    </div>
                  </td>
                  <td>
                    <span>{data.bomComponent || "--"}</span>
                  </td>
                  <td>{data?.colourRef?.newReferences || "--"}</td>
                  <td>
                    {!statusState ? (
                      <>
                        <div className="d-flex align-items-center">
                          <div className="flex-fill">
                            <input
                              onChange={(e) => {
                                if (checkIsNumber(e.target.value)) {
                                  handleInputChange(
                                    e.target.value,
                                    testingFieldpath(index).TOTAL_PRICE,
                                    testingFieldpath(index).IS_CHANGED_BY_SUPPLIER_TOTAL_PRICE,
                                    index,
                                    testingFieldpath(index).PRICE_PER_UNIT,
                                  );
                                  if (get(testing, `[${index}].totalPrice`, "")) {
                                    updateError(errorpath(index).TESTING.ERROR_TOTPRICE);
                                  }
                                }
                              }}
                              readOnly={statusState}
                              value={data.totalPrice}
                              type="text"
                              className={
                                data.isChangedBySupplierTotalPrice && controlAmberColour
                                  ? "suggested-data form-control text-bordered text-left"
                                  : "form-control text-bordered text-left"
                              }
                            />
                          </div>{" "}
                          <span className="text-nowrap ml-1">{checkCurrency(currency, undefined, true)} </span>
                        </div>
                        <span className="error-message">{get(testing, `[${index}].totalPrice`)}</span>
                      </>
                    ) : (
                      data.totalPrice
                    )}
                  </td>
                  <td className="text-left">
                    {checkCurrency(currency, data?.pricePerUnit)} {roundOff(data?.pricePerUnit, 4, true)}
                  </td>
                  <td>
                    <div className="common-checkbox">
                      <NewCheckbox
                        size="sm"
                        onChange={(e) =>
                          handleInputChange(e.target.checked, testingFieldpath(index).IS_EXCLUDED, undefined, index, testingFieldpath(index).PRICE_PER_UNIT)
                        }
                        disabled={markAsCompleteState}
                        checked={data.isExcluded}
                        id={`exclude_${index}`}
                        className={data.isChangedBySupplierPricePerUnit && controlAmberColour ? "suggested-data text-left" : "text-left"}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          <tr>
            <td colSpan="6" />
            <td colSpan="3" className="text-left">
              <span className="f-16 text-bold">
                {t("FILTER.TOTAL_PRICES")} {checkCurrency(currency, sumOfPrice)} {roundOff(sumOfPrice, 4, true)}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

export default TestingTable;
